import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { routesAPI, jobsAPI, usersAPI, customersAPI } from '@/lib/api';
import { formatDate, getStatusColor, getJobTypeColor, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft, 
  Users, 
  Calendar, 
  MapPin, 
  Package,
  ArrowUp,
  ArrowDown,
  Pencil,
  Plus,
  Trash2,
  Truck,
  CheckCircle,
  Box
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

export default function RouteDetail() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddJobDialog, setShowAddJobDialog] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const [routeForm, setRouteForm] = useState({
    name: '',
    driver_id: '',
    status: 'planned',
    notes: '',
  });
  
  const [jobForm, setJobForm] = useState({
    customer_id: '',
    job_type: 'service',
    site_address: '',
    scheduled_time_window: '',
    units_requested: 1,
    unit_type: 'standard',
    notes: '',
  });

  // Unit assignment state
  const [showUnitAssignDialog, setShowUnitAssignDialog] = useState(false);
  const [assigningJob, setAssigningJob] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [savingUnits, setSavingUnits] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDrivers();
    fetchCustomers();
  }, [id]);

  const fetchData = async () => {
    try {
      const [routeResponse, jobsResponse] = await Promise.all([
        routesAPI.getById(id),
        jobsAPI.getAll({ route_id: id }),
      ]);
      setRoute(routeResponse.data);
      setJobs(jobsResponse.data.sort((a, b) => (a.route_sequence || 0) - (b.route_sequence || 0)));
    } catch (error) {
      console.error('Error fetching route:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await usersAPI.getAll('driver');
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const openEditDialog = () => {
    setRouteForm({
      name: route.name,
      driver_id: route.driver_id || 'unassigned',
      status: route.status,
      notes: route.notes || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateRoute = async () => {
    setSaving(true);
    try {
      const driverId = routeForm.driver_id === 'unassigned' ? null : routeForm.driver_id || null;
      await routesAPI.update(id, {
        name: routeForm.name,
        driver_id: driverId,
        status: routeForm.status,
        notes: routeForm.notes || null,
      });
      toast.success('Route updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating route:', error);
      toast.error('Failed to update route');
    } finally {
      setSaving(false);
    }
  };

  const openAddJobDialog = () => {
    setJobForm({
      customer_id: '',
      job_type: 'service',
      site_address: '',
      scheduled_time_window: '',
      units_requested: 1,
      unit_type: 'standard',
      notes: '',
    });
    setShowAddJobDialog(true);
  };

  const handleAddJob = async () => {
    if (!jobForm.customer_id || !jobForm.site_address) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const nextSequence = jobs.length > 0 ? Math.max(...jobs.map(j => j.route_sequence || 0)) + 1 : 1;
      
      await jobsAPI.create({
        ...jobForm,
        scheduled_date: route.route_date,
        route_id: id,
        driver_id: route.driver_id,
        route_sequence: nextSequence,
        status: 'scheduled',
      });
      toast.success('Job added to route');
      setShowAddJobDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error('Failed to add job');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveJob = async (jobId) => {
    if (!window.confirm('Remove this job from the route?')) return;
    
    try {
      await jobsAPI.unassignFromRoute(jobId);
      toast.success('Job removed from route');
      fetchData();
    } catch (error) {
      console.error('Error removing job:', error);
      toast.error('Failed to remove job');
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setJobForm(prev => ({
      ...prev,
      customer_id: customerId,
      site_address: customer?.site_address || prev.site_address,
    }));
  };

  const moveJob = async (index, direction) => {
    const newJobs = [...jobs];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= jobs.length) return;
    
    // Swap jobs
    [newJobs[index], newJobs[newIndex]] = [newJobs[newIndex], newJobs[index]];
    
    // Update sequence numbers
    const updatedJobs = newJobs.map((job, idx) => ({
      ...job,
      route_sequence: idx + 1
    }));
    
    setJobs(updatedJobs);
    setReordering(true);
    
    try {
      await jobsAPI.reorder(updatedJobs.map(j => ({ id: j.id, route_sequence: j.route_sequence })));
      toast.success('Route order updated');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to update order');
      fetchData(); // Revert on error
    } finally {
      setReordering(false);
    }
  };

  // Open unit assignment dialog
  const openUnitAssignDialog = async (job) => {
    setAssigningJob(job);
    setSelectedUnits(job.assigned_units || []);
    setShowUnitAssignDialog(true);
    setLoadingUnits(true);

    try {
      const response = await jobsAPI.getAvailableUnits(job.id);
      setAvailableUnits(response.data.available_units || []);
    } catch (error) {
      console.error('Error loading available units:', error);
      toast.error('Failed to load available units');
    } finally {
      setLoadingUnits(false);
    }
  };

  // Toggle unit selection
  const toggleUnitSelection = (unitId) => {
    setSelectedUnits(prev => {
      if (prev.includes(unitId)) {
        return prev.filter(id => id !== unitId);
      } else {
        return [...prev, unitId];
      }
    });
  };

  // Save unit assignments
  const handleSaveUnitAssignments = async () => {
    if (!assigningJob) return;
    
    setSavingUnits(true);
    try {
      await jobsAPI.assignUnits(assigningJob.id, selectedUnits);
      toast.success(`${selectedUnits.length} unit(s) assigned to job`);
      setShowUnitAssignDialog(false);
      setAssigningJob(null);
      fetchData(); // Refresh jobs to show updated assignments
    } catch (error) {
      console.error('Error assigning units:', error);
      toast.error('Failed to assign units');
    } finally {
      setSavingUnits(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      </AdminLayout>
    );
  }

  if (!route) {
    return (
      <AdminLayout>
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Route Not Found</h3>
            <Link to="/admin/routes">
              <Button variant="outline">Back to Routes</Button>
            </Link>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/routes" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft size={16} className="mr-1" />
          Back to Routes
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="route-title">{route.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDate(route.route_date)}
              </span>
              <span className="flex items-center gap-1">
                <Users size={14} />
                {route.driver_name || 'Unassigned'}
              </span>
              <Badge className={getStatusColor(route.status)} variant="outline">
                {route.status}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={openEditDialog}
              data-testid="edit-route-btn"
            >
              <Pencil size={14} className="mr-2" />
              Edit Route
            </Button>
            {route.driver_id && (
              <Link to={`/admin/driver-view/${route.driver_id}/today`}>
                <Button className="bg-[#194461] hover:bg-[#091D2E]" data-testid="driver-view-btn">
                  Open Driver View
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Route Notes */}
      {route.notes && (
        <Card className="mb-6 border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600">{route.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Jobs List */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between">
            <span>Stops ({jobs.length})</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-slate-500 hidden sm:inline">
                Use arrows to reorder
              </span>
              <Button 
                size="sm"
                onClick={openAddJobDialog}
                className="bg-[#194461] hover:bg-[#091D2E]"
                data-testid="add-job-btn"
              >
                <Plus size={14} className="mr-1" />
                Add Stop
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8">
              <Package className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-slate-500">No stops on this route</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job, index) => (
                <div 
                  key={job.id}
                  className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
                  data-testid={`job-card-${job.id}`}
                >
                  {/* Reorder Controls */}
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <span className="text-lg font-bold text-slate-400 mb-1">{index + 1}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveJob(index, 'up')}
                      disabled={index === 0 || reordering}
                      data-testid={`move-up-${job.id}`}
                    >
                      <ArrowUp size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveJob(index, 'down')}
                      disabled={index === jobs.length - 1 || reordering}
                      data-testid={`move-down-${job.id}`}
                    >
                      <ArrowDown size={14} />
                    </Button>
                  </div>

                  {/* Job Details */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={getJobTypeColor(job.job_type)} variant="outline">
                        {job.job_type}
                      </Badge>
                      <Badge className={getStatusColor(job.status)} variant="outline">
                        {job.status.replace('_', ' ')}
                      </Badge>
                      {job.scheduled_time_window && (
                        <span className="text-xs text-slate-500">{job.scheduled_time_window}</span>
                      )}
                    </div>

                    <h4 className="font-medium text-slate-900 mb-1">{job.customer_name}</h4>
                    
                    <div className="flex items-start gap-1 text-sm text-slate-600 mb-2">
                      <MapPin size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
                      <span>{job.site_address}</span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {job.units_requested} {job.unit_type}
                      </span>
                      {/* Show assigned units */}
                      {job.assigned_unit_codes && job.assigned_unit_codes.length > 0 ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle size={14} />
                          {job.assigned_unit_codes.join(', ')}
                        </span>
                      ) : (
                        <button
                          onClick={() => openUnitAssignDialog(job)}
                          className="text-[#52B8E1] hover:text-[#194461] flex items-center gap-1 transition-colors"
                          data-testid={`assign-units-${job.id}`}
                        >
                          <Box size={14} />
                          Assign Units
                        </button>
                      )}
                    </div>

                    {/* Edit assigned units button when units are already assigned */}
                    {job.assigned_unit_codes && job.assigned_unit_codes.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 h-7 text-xs text-slate-500 hover:text-[#194461]"
                        onClick={() => openUnitAssignDialog(job)}
                        data-testid={`edit-units-${job.id}`}
                      >
                        <Pencil size={12} className="mr-1" />
                        Edit Units
                      </Button>
                    )}

                    {job.notes && (
                      <p className="mt-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                        {job.notes}
                      </p>
                    )}
                  </div>

                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-red-600 flex-shrink-0"
                    onClick={() => handleRemoveJob(job.id)}
                    data-testid={`remove-job-${job.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Route Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Route</DialogTitle>
            <DialogDescription>
              Update route details and assignments
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit_name">Route Name *</Label>
              <Input
                id="edit_name"
                value={routeForm.name}
                onChange={(e) => setRouteForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="edit-route-name"
              />
            </div>

            <div>
              <Label htmlFor="edit_driver">Assigned Driver</Label>
              <Select 
                value={routeForm.driver_id} 
                onValueChange={(value) => setRouteForm(prev => ({ ...prev, driver_id: value }))}
              >
                <SelectTrigger data-testid="edit-route-driver">
                  <SelectValue placeholder="Select a driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select 
                value={routeForm.status} 
                onValueChange={(value) => setRouteForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="edit-route-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={routeForm.notes}
                onChange={(e) => setRouteForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="edit-route-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateRoute}
              disabled={saving || !routeForm.name}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-route-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Job Dialog */}
      <Dialog open={showAddJobDialog} onOpenChange={setShowAddJobDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Stop to Route</DialogTitle>
            <DialogDescription>
              Add a new job/stop to this route
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="customer">Customer *</Label>
              <Select 
                value={jobForm.customer_id} 
                onValueChange={handleCustomerChange}
              >
                <SelectTrigger data-testid="select-customer">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="job_type">Job Type *</Label>
              <Select 
                value={jobForm.job_type} 
                onValueChange={(value) => setJobForm(prev => ({ ...prev, job_type: value }))}
              >
                <SelectTrigger data-testid="select-job-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="site_address">Site Address *</Label>
              <Input
                id="site_address"
                value={jobForm.site_address}
                onChange={(e) => setJobForm(prev => ({ ...prev, site_address: e.target.value }))}
                placeholder="123 Main St, City, TN"
                data-testid="input-site-address"
              />
            </div>

            <div>
              <Label htmlFor="time_window">Time Window</Label>
              <Input
                id="time_window"
                value={jobForm.scheduled_time_window}
                onChange={(e) => setJobForm(prev => ({ ...prev, scheduled_time_window: e.target.value }))}
                placeholder="9:00 AM - 11:00 AM"
                data-testid="input-time-window"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="units_requested">Units</Label>
                <Input
                  id="units_requested"
                  type="number"
                  min="1"
                  value={jobForm.units_requested}
                  onChange={(e) => setJobForm(prev => ({ ...prev, units_requested: parseInt(e.target.value) || 1 }))}
                  data-testid="input-units"
                />
              </div>
              <div>
                <Label htmlFor="unit_type">Unit Type</Label>
                <Select 
                  value={jobForm.unit_type} 
                  onValueChange={(value) => setJobForm(prev => ({ ...prev, unit_type: value }))}
                >
                  <SelectTrigger data-testid="select-unit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="ADA">ADA</SelectItem>
                    <SelectItem value="handwash">Handwash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="job_notes">Notes</Label>
              <Textarea
                id="job_notes"
                value={jobForm.notes}
                onChange={(e) => setJobForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Special instructions..."
                rows={2}
                data-testid="input-job-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddJobDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddJob}
              disabled={saving || !jobForm.customer_id || !jobForm.site_address}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-job-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit Assignment Dialog */}
      <Dialog open={showUnitAssignDialog} onOpenChange={(open) => {
        if (!open) {
          setShowUnitAssignDialog(false);
          setAssigningJob(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={20} className="text-[#52B8E1]" />
              Assign Units to Job
            </DialogTitle>
            <DialogDescription>
              {assigningJob && (
                <>
                  {assigningJob.job_type.charAt(0).toUpperCase() + assigningJob.job_type.slice(1)} for {assigningJob.customer_name}
                  <br />
                  <span className="text-slate-400">{assigningJob.units_requested} unit(s) requested</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingUnits ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#52B8E1]" />
              </div>
            ) : availableUnits.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="mx-auto mb-2 opacity-30" size={32} />
                <p>No available units found</p>
                <p className="text-xs mt-1">
                  {assigningJob?.job_type === 'delivery' 
                    ? 'Make sure there are units in the yard matching the required type.'
                    : 'Units may already be assigned to the delivery job.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <p className="text-sm text-slate-600 mb-3">
                  Select {assigningJob?.units_requested || 1} unit(s):
                  <span className="text-slate-400 ml-2">
                    ({selectedUnits.length} selected)
                  </span>
                </p>
                
                {availableUnits.map((unit) => {
                  const isSelected = selectedUnits.includes(unit.id);
                  return (
                    <div
                      key={unit.id}
                      onClick={() => toggleUnitSelection(unit.id)}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-[#52B8E1] bg-[#52B8E1]/5' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`unit-option-${unit.id}`}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleUnitSelection(unit.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{unit.unit_code}</span>
                          <Badge variant="outline" className="text-xs">
                            {unit.type}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              unit.status === 'in_yard' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              unit.status === 'on_rent' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                          >
                            {unit.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        {unit.current_location && (
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <MapPin size={10} />
                            {unit.current_location}
                          </p>
                        )}
                        {unit.last_service_date && (
                          <p className="text-xs text-slate-400">
                            Last service: {formatDate(unit.last_service_date)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedUnits.length !== (assigningJob?.units_requested || 1) && selectedUnits.length > 0 && (
              <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                <Package size={12} />
                {selectedUnits.length < (assigningJob?.units_requested || 1)
                  ? `${(assigningJob?.units_requested || 1) - selectedUnits.length} more unit(s) needed`
                  : `${selectedUnits.length - (assigningJob?.units_requested || 1)} more than requested`
                }
              </p>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUnitAssignDialog(false);
                setAssigningJob(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUnitAssignments}
              disabled={savingUnits || selectedUnits.length === 0}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-unit-assignment-btn"
            >
              {savingUnits ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle size={14} className="mr-2" />
              )}
              Assign {selectedUnits.length} Unit(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
